<template>
  <div v-click-outside="closeSuggest" class="autocomplete-dropdown-v2">
    <div v-if="label" :class="labelClass">
      {{ label }}
    </div>
    <div v-if="label && appendText" class="autocomplete-dropdown-v2__append-text">{{ appendText }}</div>
    <BravadoTextInputV3
      ref="input"
      v-model="query"
      type="text"
      :variant="variant"
      :placeholder="placeholder"
      autocomplete="off"
      :name="name"
      :is-valid="isAutocompleteValid"
      :disabled="disabled"
      @keydown.enter="handleEnterPress($event)"
      @focus="openSuggest"
      @blur="handleBlur"
      @keyup.down="focus(1)"
      @keyup.up="focus(-1)"
      @keyup.delete="focusReset"
      v-on="$listeners"
      data-test="autocomplete-dropdown-v2__result-item"
    >
      <template v-for="(index, slotName) in $slots" #[slotName]>
        <slot :name="slotName" />
      </template>
    </BravadoTextInputV3>
    <div v-if="errorMessage.length" class="autocomplete-dropdown-v2__error" data-test="autocomplete-dropdown-v2__error">
      {{ errorMessage }}
    </div>
    <div v-if="showSuggest" class="autocomplete-dropdown-v2__results-list">
      <div
        v-for="(item, index) in items"
        :key="item.id"
        data-test="autocomlete-item-block"
        :class="[resultClass, { 'autocomplete-dropdown-v2__results-element--selected': focused === index }]"
        @click="clickItem(item)"
      >
        <slot name="item" :index="index" :item="item" :withHighlight="withHighlight" />
      </div>
      <div
        v-if="$slots['last-item']"
        class="autocomplete-dropdown-v2__results-last"
        @click="clickItem({ isNewCompany: true, name: query })"
      >
        <slot name="last-item" />
      </div>
    </div>
    <div v-if="$slots['append-icon']" class="autocomplete-dropdown-v2__append-icon">
      <slot name="append-icon" />
    </div>
  </div>
</template>

<script>
import { escapeRegExp } from '@/components/utils/core/regexp';
import clickOutside from '~/directives/click_outside';
import BravadoTextInputV3 from '~/components/common/BravadoTextInputV3/BravadoTextInputV3';

export default {
  name: 'AutocompleteDropdownV2',
  components: {
    BravadoTextInputV3,
  },
  directives: {
    'click-outside': clickOutside,
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    initialQuery: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: '',
    },
    textField: {
      type: String,
      default: 'text',
    },
    valueField: {
      type: String,
      default: 'value',
    },
    isValid: {
      type: Boolean,
      default: true,
    },
    errorMessage: {
      type: String,
      default: '',
    },
    resultsItemClass: {
      type: [String, Object],
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    useTextInputV3: {
      type: Boolean,
      default: false,
    },
    appendText: {
      type: String,
      default: '',
    },
    variant: {
      type: String,
      default: 'default',
      validator: value => ['default', 'borderless'].includes(value),
    },
    size: {
      type: String,
      default: 'md',
      validator: value => ['lg', 'md'].includes(value),
    },
  },
  data() {
    return {
      opened: false,
      focused: -1,
      query: '',
      preventOpen: false,
      queryTimeout: null,
      queryChanged: null,
    };
  },
  computed: {
    isAutocompleteValid() {
      return !this.isValid ? false : !this.errorMessage.length;
    },
    resultClass() {
      return ['autocomplete-dropdown-v2__results-element', this.resultsItemClass];
    },
    labelClass() {
      return {
        'autocomplete-dropdown-v2__label': true,
        [`autocomplete-dropdown-v2__label--${this.size}`]: true,
      };
    },
    showSuggest() {
      return (this.items.length > 0 || this.$slots['last-item']) && this.opened;
    },
  },
  watch: {
    query(query) {
      if (this.preventOpen) {
        this.preventOpen = false;
        return;
      }

      clearTimeout(this.queryTimeout);

      // prevent open after init load items
      this.queryChanged = this.queryChanged !== null;

      this.queryTimeout = setTimeout(() => {
        this.$emit('query', { text: query, value: query });
      }, 200);
    },
    items() {
      if (this.queryChanged) {
        this.queryChanged = false;
        this.openSuggest();
      }
    },
    initialQuery() {
      this.$nextTick(() => {
        if (this.query !== this.initialQuery) {
          this.preventOpen = true;
          this.query = this.initialQuery;
        }
      });
    },
  },
  mounted() {
    this.query = this.initialQuery || '';
  },
  methods: {
    openSuggest(e) {
      this.opened = true;
      this.$emit('focus', e);
    },
    closeSuggest() {
      this.opened = false;
    },
    withHighlight(string = '', search) {
      const searchRE = new RegExp(`(${escapeRegExp(search)})`, 'ig');
      return search ? string.replace(searchRE, `<span class="members-user-block-highlight">$1</span>`) : string;
    },
    focusReset() {
      this.focused = -1;
    },
    focus(val) {
      const newFocus = this.focused + val;

      if (newFocus >= 0 && newFocus < this.items.length) this.focused = newFocus;
    },
    handleEnterPress(event) {
      // allow to select via enter if focused. Otherwise prevent if no query or queryChanged
      if (this.focused < 0 && (!this.query || this.queryChanged)) {
        return;
      }
      // Prevent only if user have a dropdown, becuase we still must have enter handler on whole form
      event.preventDefault();

      // FIXME: that's not a right way: because we must not to lost focus on a fields
      //        otherwise you can't send a form by keys, becuase form isn't in focus
      this.$refs.input.blur();

      let item;
      if (this.items[this.focused]) {
        item = this.items[this.focused];
      } else if (this.focused === -1) {
        item = {
          text: this.query,
          value: this.query,
        };
      } else {
        item = this.items[0] || {
          text: this.query,
          value: this.query,
        };
      }

      this.clickItem(item);
    },
    handleBlur() {
      // Close suggestion with delay, becuase in somecases we still work
      // with autocomplete after blur
      setTimeout(() => {
        this.closeSuggest();
      }, 500);
    },
    clickItem(item) {
      const event = {
        ...item,
        text: item.text || item[this.textField],
        value: item.value || item[this.valueField],
      };

      this.$emit('push', event);
      this.closeSuggest();
    },
    focusInput() {
      this.$refs.input.focus();
    },
  },
};
</script>

<style scoped lang="scss">
.autocomplete-dropdown-v2 {
  position: relative;
  width: 100%;

  &__results-list {
    position: absolute;
    width: 100%;
    max-height: 250px;
    background: var(--theme-color-background);
    overflow-y: auto;
    overflow-x: hidden;
    z-index: 120;
    left: 0;
    top: 100%;
    border: 1px solid var(--theme-color-background);
    box-shadow: var(--theme-elevation-01);
    border-radius: 8px;
    margin-top: 2px;
  }

  &__results-element {
    color: var(--theme-color-text);
    background: var(--theme-color-card);

    &--selected {
      background-color: var(--theme-card-hover);
    }

    &:hover {
      color: var(--theme-color-text-hover);
      background-color: var(--theme-color-over-card);
      cursor: pointer;
    }
  }

  &__field {
    @include text-overflow;
    @include body;

    resize: none;
    width: 100%;
    background: var(--theme-color-card);
    border: 1px solid var(--theme-color-border);
    color: var(--theme-color-text);
    border-radius: 6px;
    padding: 8px 12px;
    outline: none;
    appearance: none;

    &--error {
      border-color: var(--universal-color-danger-hover);
    }
  }

  &__label {
    color: var(--theme-color-text);
    margin-bottom: 6px;

    &--md {
      @include subhead-semibold;
    }

    &--lg {
      @include body-semibold;
    }
  }

  &__error {
    @include caption2;

    color: var(--universal-color-danger-hover);
    margin-top: 4px;
  }

  &__append-text {
    color: var(--theme-color-sub-text);
    position: absolute;
    top: 0;
    right: 0;
  }

  &__append-icon {
    position: absolute;
    display: inline-flex;
    bottom: 13px;
    right: 12px;
    cursor: pointer;
    color: var(--theme-color-sub-text);
  }

  &__results-last {
    position: sticky;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: inherit;
  }
}
</style>
